/**
 * Locale Initializer Component
 * Detects user's country (for currency/pricing) and keeps i18n in sync with Zustand.
 * Note: language is driven by Zustand (single source of truth). See i18n.js.
 */

import { useEffect, useRef } from 'react';
import axios from 'axios';
import i18n from '../../i18n';
import useLocaleStore from '../../store/useLocaleStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LocaleInitializer = ({ children }) => {
  const initialized = useRef(false);
  const { setDetectedCountry, setCustomPrices, language } = useLocaleStore();

  // Keep i18n in sync whenever the Zustand language changes (user picked a
  // new flag in the LocaleSelector). setLanguage already calls
  // i18n.changeLanguage; this is a belt-and-suspenders safety net for any
  // path that mutates `language` directly (e.g. rehydration on tab focus).
  useEffect(() => {
    if (language && i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const detectAndInitialize = async () => {
      try {
        // Detect country (used for currency/pricing — NOT for language).
        // Language is driven by the user's explicit choice (or the 'pt' default).
        const detectResponse = await axios.get(`${API}/locale/detect`);
        if (detectResponse.data.detected) {
          setDetectedCountry(detectResponse.data.country_code);
        }

        // Fetch pricing for detected country
        const pricingResponse = await axios.get(`${API}/locale/pricing`, {
          params: { country_code: detectResponse.data.country_code }
        });

        if (pricingResponse.data.custom) {
          setCustomPrices({
            [detectResponse.data.country_code]: pricingResponse.data.prices
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.log('Locale detection failed, using defaults');
      }
    };

    detectAndInitialize();
  }, [setDetectedCountry, setCustomPrices]);

  return children;
};

export default LocaleInitializer;
